import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as ExpenseIcon,
  MonetizationOn as MoneyIcon,
  CheckCircle as ApprovedIcon,
  Schedule as PendingIcon,
  Cancel as RejectedIcon,
  Assignment as ReportIcon,
  FlightTakeoff as TravelIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveCircleIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { format, parseISO } from "date-fns";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/slices/authSlice";
import { useAuth } from "../../components/features/auth/AuthGuard";
import useNotification from "../../hooks/common/useNotification";
import { LoadingSpinner } from "../../components/common/Loading";
import { travelAPI } from "../../services/api/travel.api";
import travelExpenseReportsAPI from "../../services/api/travelExpenseReports.api";
import { documentsAPI } from "../../services/api/documents.api";

const TravelExpenseReportsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector(selectUser);
  const { hasPermission, hasRole } = useAuth();
  const { showSuccess, showError } = useNotification();

  // Check if this is an approval view
  const isApprovalView = searchParams.get("view") === "approvals";

  // Only the advance owner can create their own expense report; supervisors may only approve/reject.
  const canCreateExpenseReport =
    hasRole("Admin") || !hasRole("Department Head");

  // State management
  const [expenseReports, setExpenseReports] = useState([]);
  const [travelRequests, setTravelRequests] = useState([]);
  const [travelAdvances, setTravelAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  // Debounce search so we don't fetch on every keystroke
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDetails, setReportDetails] = useState(null);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState({ open: false, blobUrl: null, filename: "", isImage: false, isPdf: false });
  const [docLoading, setDocLoading] = useState({}); // { [documentId-action]: true }
  const [anchorEl, setAnchorEl] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [approvalComments, setApprovalComments] = useState("");

  // Form state
  const [formData, setFormData] = useState({
    travel_request_id: "",
    travel_advance_request_id: "",
    expense_title: "",
    description: "",
    currency: "TZS",
    destination: "",
    travel_purpose: "",
  });

  const receiptInputRef = useRef(null);

  // Price tracking: summary for selected advance (advance_total, already_reported, remaining)
  const [advanceSummary, setAdvanceSummary] = useState(null);
  // When editing existing draft (same travel): report id so we update instead of create
  const [editingDraftId, setEditingDraftId] = useState(null);

  // Expense lines state
  const [expenseLines, setExpenseLines] = useState([]);
  const [newExpenseLine, setNewExpenseLine] = useState({
    title: "",
    category: "",
    amount: "",
    location: "",
    has_receipt: true,
    receiptFile: null,
    receiptFileName: "",
  });

  // Only the report owner can fill/edit/submit their expense report.
  const isReportOwner = (report) =>
    report && user?.id != null && Number(report.created_by) === Number(user.id);

  // Status definitions (must include backend values: draft, submitted, department_head_approved, approved, rejected)
  const reportStatuses = {
    draft: { label: "Draft", color: "default" },
    submitted: { label: "Submitted", color: "info" },
    pending: { label: "Pending", color: "warning" },
    department_head_approved: { label: "Dept Head Approved", color: "info" },
    approved: { label: "Approved", color: "success" },
    rejected: { label: "Rejected", color: "error" },
  };

  // Expense categories
  const expenseCategories = [
    { value: "accommodation", label: "Accommodation" },
    { value: "transport", label: "Transport" },
    { value: "meals", label: "Meals" },
    { value: "fuel", label: "Fuel" },
    { value: "communication", label: "Communication" },
    { value: "supplies", label: "Supplies" },
    { value: "other", label: "Other" },
  ];

  const fetchExpenseReports = async () => {
    try {
      setLoading(true);
      let response;

      if (isApprovalView) {
        // Fetch approval queue
        response =
          await travelExpenseReportsAPI.getExpenseReportApprovalQueue();
        setExpenseReports(response.data.data?.expenseReports || []);
      } else {
        // Fetch all expense reports (search and status filter applied server-side). _t bypasses cache so list updates after submit/save.
        response =
          await travelExpenseReportsAPI.getExpenseReportsWithTravelData({
            search: debouncedSearchTerm,
            status: statusFilter,
            page: 1,
            limit: 50,
            _t: Date.now(),
          });
        setExpenseReports(response.data.data?.expenseReports || []);
      }
    } catch (error) {
      console.error("Error fetching expense reports:", error);
      showError("Failed to fetch expense reports");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when view, search, or status filter changes
  useEffect(() => {
    fetchExpenseReports();
    if (!isApprovalView) {
      fetchTravelRequests();
      fetchTravelAdvances();
    }
  }, [isApprovalView, debouncedSearchTerm, statusFilter]);

  // Fetch full report (with expense lines) when View Details dialog opens
  useEffect(() => {
    if (!viewDialogOpen || !selectedReport?.id) {
      setReportDetails(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setViewDetailsLoading(true);
      setReportDetails(null);
      try {
        const response = await travelExpenseReportsAPI.getExpenseReportById(selectedReport.id);
        const data = response?.data?.data;
        if (!cancelled && data) setReportDetails(data);
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching expense report details:", err);
          showError("Failed to load report details");
        }
      } finally {
        if (!cancelled) setViewDetailsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [viewDialogOpen, selectedReport?.id]);

  const handlePreviewReceipt = async (documentId, filename) => {
    if (!documentId) return;
    const key = `${documentId}-preview`;
    setDocLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const blob = await documentsAPI.download(documentId);
      const url = URL.createObjectURL(blob);
      const isImage = blob.type && blob.type.startsWith("image/");
      const isPdf = blob.type === "application/pdf" || (filename && /\.pdf$/i.test(filename));
      setPreviewDoc({ open: true, blobUrl: url, filename: filename || "document", isImage, isPdf });
    } catch (err) {
      console.error("Error loading document:", err);
      showError(err.message || "Failed to load document preview");
    } finally {
      setDocLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleClosePreview = () => {
    if (previewDoc.blobUrl) URL.revokeObjectURL(previewDoc.blobUrl);
    setPreviewDoc({ open: false, blobUrl: null, filename: "", isImage: false, isPdf: false });
  };

  const handleDownloadReceipt = async (documentId, filename) => {
    if (!documentId) return;
    const key = `${documentId}-download`;
    setDocLoading((prev) => ({ ...prev, [key]: true }));
    try {
      const blob = await documentsAPI.download(documentId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "receipt";
      a.click();
      URL.revokeObjectURL(url);
      showSuccess("Download started");
    } catch (err) {
      console.error("Error downloading document:", err);
      showError(err.message || "Failed to download document");
    } finally {
      setDocLoading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const fetchTravelRequests = async () => {
    try {
      const response =
        await travelExpenseReportsAPI.getTravelRequestsForExpenseReports();
      const raw = response?.data?.data;
      const list = Array.isArray(raw?.travelRequests)
        ? raw.travelRequests
        : Array.isArray(raw)
          ? raw
          : [];
      setTravelRequests(list);
    } catch (error) {
      console.error("Error fetching travel requests:", error);
      setTravelRequests([]);
    }
  };

  const fetchTravelAdvances = async () => {
    try {
      const response = await travelAPI.getAllAdvances();
      setTravelAdvances(response.data.data?.advances || []);
    } catch (error) {
      console.error("Error fetching travel advances:", error);
    }
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Fetch advance summary and load draft if one exists for this travel (so user edits draft instead of creating another)
  const fetchAdvanceSummaryAndMaybeLoadDraft = async (advanceId, travelRequest) => {
    if (!advanceId) {
      setAdvanceSummary(null);
      setEditingDraftId(null);
      setExpenseLines([]);
      return;
    }
    try {
      const response = await travelExpenseReportsAPI.getExpenseReportSummaryByAdvance(advanceId);
      const raw = response?.data?.data;
      if (raw) {
        setAdvanceSummary({
          advance_total: Number(raw.advance_total) || 0,
          already_reported: Number(raw.already_reported) || 0,
          remaining: Number(raw.remaining) || 0,
          reports_count: Number(raw.reports_count) || 0,
          draft_report_id: raw.draft_report_id || null,
        });
        if (raw.draft_report_id) {
          const reportRes = await travelExpenseReportsAPI.getExpenseReportById(raw.draft_report_id);
          const report = reportRes?.data?.data;
          if (report) {
            setFormData((prev) => ({
              ...prev,
              travel_request_id: report.travel_request_id || travelRequest?.id,
              travel_advance_request_id: advanceId,
              destination: report.destination ?? travelRequest?.destination,
              travel_purpose: travelRequest?.purpose ?? prev.travel_purpose,
              expense_title: report.expense_title || prev.expense_title,
              description: report.description || "",
            }));
            setExpenseLines(
              (Array.isArray(report.expenseLines) ? report.expenseLines : []).map((l, i) => ({
                id: l?.id ?? i,
                title: String(l?.title ?? ""),
                category: String(l?.category ?? ""),
                amount: l?.amount != null ? Number(l.amount) : 0,
                location: String(l?.location ?? ""),
                has_receipt: l?.has_receipt !== false,
                receiptFile: null,
                receiptFileName: String(l?.receipt_filename ?? l?.receiptFileName ?? ""),
              }))
            );
            setEditingDraftId(report.id);
            return;
          }
        }
      } else {
        setAdvanceSummary(null);
      }
      setEditingDraftId(null);
      setExpenseLines([]);
    } catch (error) {
      console.error("Error fetching advance summary:", error);
      setAdvanceSummary(null);
      setEditingDraftId(null);
      setExpenseLines([]);
    }
  };

  // Handle travel request selection
  const handleTravelRequestSelect = (travelRequest) => {
    const relatedAdvance = travelAdvances.find(
      (advance) => advance.travel_request_id === travelRequest.id
    );
    const advanceId = relatedAdvance?.id || "";

    setFormData((prev) => ({
      ...prev,
      travel_request_id: travelRequest.id,
      travel_advance_request_id: advanceId,
      destination: travelRequest.destination,
      travel_purpose: travelRequest.purpose,
      expense_title: `Expense Report - ${travelRequest.destination || travelRequest.purpose || `Travel #${travelRequest.id}`}`,
    }));
    fetchAdvanceSummaryAndMaybeLoadDraft(advanceId, travelRequest);
  };

  // Handle expense line changes
  const handleExpenseLineChange = (field, value) => {
    setNewExpenseLine((prev) => ({ ...prev, [field]: value }));
  };

  // Add expense line (include receipt file reference for later base64 in payload)
  const addExpenseLine = () => {
    if (!newExpenseLine.title || !newExpenseLine.amount) {
      showError("Please fill in title and amount");
      return;
    }

    // Build a new object explicitly so the first row always has category, location, receipt
    const expenseLine = {
      id: Date.now() + Math.random(),
      title: String(newExpenseLine.title || "").trim(),
      category: String(newExpenseLine.category ?? ""),
      amount: parseFloat(newExpenseLine.amount) || 0,
      location: String(newExpenseLine.location ?? ""),
      has_receipt: newExpenseLine.has_receipt !== false,
      receiptFile: newExpenseLine.receiptFile || null,
      receiptFileName: String(newExpenseLine.receiptFileName ?? ""),
    };

    setExpenseLines((prev) => [...prev, expenseLine]);
    setNewExpenseLine({
      title: "",
      category: "",
      amount: "",
      location: "",
      has_receipt: true,
      receiptFile: null,
      receiptFileName: "",
    });
  };

  // Convert File to base64 for payload
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (e) => reject(e);
    });

  // Remove expense line
  const removeExpenseLine = (id) => {
    setExpenseLines((prev) => prev.filter((line) => line.id !== id));
  };

  // Calculate totals (use advanceSummary for price tracking when adding another report for same travel)
  const calculateTotals = () => {
    const totalExpense = expenseLines.reduce(
      (sum, line) => sum + (line.amount || 0),
      0
    );
    const advanceAmount = advanceSummary
      ? advanceSummary.advance_total
      : formData.travel_advance_request_id
        ? travelAdvances.find(
            (adv) => adv.id === formData.travel_advance_request_id
          )?.total_cost || 0
        : 0;
    const alreadyReported = advanceSummary ? advanceSummary.already_reported : 0;
    const remainingBeforeThis = advanceAmount - alreadyReported;
    const remainingAfterThis = remainingBeforeThis - totalExpense;

    return {
      totalExpense,
      advanceAmount,
      alreadyReported,
      remainingBeforeThis,
      remainingAfterThis,
      remaining: remainingAfterThis, // for backward compatibility
    };
  };

  // Build payload for create (shared by draft and submit); attach receipt as base64 per line
  const buildReportPayload = async () => {
    const totals = calculateTotals();
    const linesWithReceipts = await Promise.all(
      expenseLines.map(async (line) => {
        const payloadLine = {
          title: String(line?.title ?? "").trim(),
          category: String(line?.category ?? ""),
          amount: line?.amount != null ? Number(line.amount) : 0,
          location: String(line?.location ?? ""),
          has_receipt: line?.has_receipt !== false,
        };
        // Send line id when updating draft so backend can preserve receipt by line id (not index)
        if (editingDraftId && line?.id != null && Number.isInteger(Number(line.id)) && Number(line.id) > 0) {
          payloadLine.id = Number(line.id);
        }
        if (line?.receiptFile && line.receiptFile instanceof File) {
          try {
            payloadLine.receipt_base64 = await fileToBase64(line.receiptFile);
            payloadLine.receipt_filename = line.receiptFileName || line.receiptFile.name || "receipt";
          } catch (e) {
            console.warn("Receipt to base64 failed for line:", line.id, e);
          }
        }
        return payloadLine;
      })
    );
    return {
      ...formData,
      expense_lines: linesWithReceipts,
      total_expense_amount: totals.totalExpense,
      total_advance_amount: totals.advanceAmount,
      remaining_amount: totals.remaining,
    };
  };

  // Save as draft: update existing draft or create new report
  const handleSaveDraft = async () => {
    try {
      if (!formData.travel_request_id) {
        showError("Please select a travel request");
        return;
      }
      if (expenseLines.length === 0) {
        showError("Please add at least one expense line");
        return;
      }
      setLoading(true);
      const payload = await buildReportPayload();
      if (editingDraftId) {
        await travelExpenseReportsAPI.updateExpenseReport(editingDraftId, {
          expense_title: formData.expense_title,
          description: formData.description,
          expense_lines: payload.expense_lines,
        });
        showSuccess("Draft expense report updated");
      } else {
        await travelExpenseReportsAPI.createExpenseReportFromTravel({ ...payload, status: "draft" });
        showSuccess("Expense report saved as draft");
      }
      setDialogOpen(false);
      resetForm();
      fetchExpenseReports();
    } catch (error) {
      console.error("Error saving draft:", error);
      showError(error.response?.data?.message || "Failed to save draft");
    } finally {
      setLoading(false);
    }
  };

  // Submit for approval: update draft then submit, or create then submit
  const handleSubmitForApproval = async () => {
    try {
      if (!formData.travel_request_id) {
        showError("Please select a travel request");
        return;
      }
      if (expenseLines.length === 0) {
        showError("Please add at least one expense line");
        return;
      }
      setLoading(true);
      const payload = await buildReportPayload();
      let reportId = editingDraftId;
      if (editingDraftId) {
        await travelExpenseReportsAPI.updateExpenseReport(editingDraftId, {
          expense_title: formData.expense_title,
          description: formData.description,
          expense_lines: payload.expense_lines,
        });
      } else {
        const createRes = await travelExpenseReportsAPI.createExpenseReportFromTravel(payload);
        reportId = createRes?.data?.data?.id ?? createRes?.data?.id;
      }
      if (reportId) {
        await travelExpenseReportsAPI.submitExpenseReport(reportId);
        showSuccess("Expense report submitted for approval");
        // Update list so status shows Submitted immediately (refetch below also gets fresh data with cache-bust)
        setExpenseReports((prev) =>
          prev.map((r) => (String(r.id) === String(reportId) ? { ...r, status: "submitted" } : r))
        );
      } else {
        showSuccess("Expense report created and submitted");
      }
      setDialogOpen(false);
      resetForm();
      fetchExpenseReports();
    } catch (error) {
      console.error("Error submitting expense report:", error);
      showError(error.response?.data?.message || "Failed to submit for approval");
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setAdvanceSummary(null);
    setEditingDraftId(null);
    setFormData({
      travel_request_id: "",
      travel_advance_request_id: "",
      expense_title: "",
      description: "",
      currency: "TZS",
      destination: "",
      travel_purpose: "",
    });
    setExpenseLines([]);
    setNewExpenseLine({
      title: "",
      category: "",
      amount: "",
      location: "",
      has_receipt: true,
      receiptFile: null,
      receiptFileName: "",
    });
  };

  // DataGrid columns
  const columns = [
    {
      field: "report_number",
      headerName: "Report #",
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ReportIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "advance_number",
      headerName: "Travel Advance",
      width: 200,
      minWidth: 160,
      renderCell: (params) => {
        const advNum = params.value;
        // Strip timestamp suffix: "TA-6-1769719841859" → "TA-6"
        const cleanId = advNum
          ? advNum.split("-").slice(0, 2).join("-")
          : null;
        const destination = params.row.destination;
        if (!cleanId) {
          return (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          );
        }
        return (
          <Box>
            <Typography variant="body2" fontWeight="medium" title={advNum}>
              {cleanId}
            </Typography>
            {destination && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                {destination}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "expense_title",
      headerName: "Title",
      width: 250,
    },
    {
      field: "destination",
      headerName: "Destination",
      width: 150,
    },
    {
      field: "amounts",
      headerName: "Amounts",
      width: 260,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
            "& > .amount-line": {
              display: "block",
              px: 1,
              py: 0.5,
              borderBottom: 1,
              borderColor: "divider",
              "&:last-of-type": { borderBottom: "none" },
            },
          }}
        >
          <Typography variant="body2" color="text.secondary" className="amount-line">
            Advance: TZS {params.row.total_advance_amount?.toLocaleString() ?? "0"}
          </Typography>
          <Typography variant="body2" color="text.secondary" className="amount-line">
            Spent: TZS {params.row.total_expense_amount?.toLocaleString() ?? "0"}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="medium"
            className="amount-line"
            color={
              params.row.remaining_amount >= 0 ? "success.main" : "error.main"
            }
          >
            Balance: TZS {params.row.remaining_amount?.toLocaleString() ?? "0"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const status = reportStatuses[params.value] || reportStatuses.draft;
        return (
          <Chip
            label={status.label}
            color={status.color}
            size="small"
            variant={
              (isApprovalView &&
                (params.value === "pending" ||
                  params.value === "department_head_approved" ||
                  params.value === "finance_manager_approved")) ||
              (!isApprovalView &&
                (params.value === "rejected" || params.value === "draft"))
                ? "filled"
                : "outlined"
            }
          />
        );
      },
    },
    {
      field: "created_at",
      headerName: "Created",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {format(parseISO(params.value), "dd/MM/yyyy")}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleActionClick(e, params.row)}
          size="small"
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  // Handle action menu
  const handleActionClick = (event, report) => {
    setSelectedReport(report);
    setAnchorEl(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setAnchorEl(null);
  };

  // Open dialog with draft loaded for editing (from table row action)
  const handleEditDraft = async (report) => {
    handleActionMenuClose();
    if (report?.status !== "draft") return;
    try {
      const res = await travelExpenseReportsAPI.getExpenseReportById(report.id);
      const data = res?.data?.data;
      if (!data) {
        showError("Failed to load report");
        return;
      }
      const travelRequest = travelRequests.find((tr) => tr.id === data.travel_request_id);
      setFormData({
        travel_request_id: data.travel_request_id || "",
        travel_advance_request_id: data.travel_advance_request_id || "",
        expense_title: data.expense_title || "",
        description: data.description || "",
        destination: data.destination || "",
        travel_purpose: travelRequest?.purpose || "",
        currency: "TZS",
      });
      setExpenseLines(
        (Array.isArray(data.expenseLines) ? data.expenseLines : []).map((l, i) => ({
          id: l?.id ?? i,
          title: String(l?.title ?? ""),
          category: String(l?.category ?? ""),
          amount: l?.amount != null ? Number(l.amount) : 0,
          location: String(l?.location ?? ""),
          has_receipt: l?.has_receipt !== false,
          receiptFile: null,
          receiptFileName: String(l?.receipt_filename ?? l?.receiptFileName ?? ""),
        }))
      );
      setEditingDraftId(data.id);
      if (data.travel_advance_request_id) {
        try {
          const sumRes = await travelExpenseReportsAPI.getExpenseReportSummaryByAdvance(data.travel_advance_request_id);
          const raw = sumRes?.data?.data;
          if (raw) {
            setAdvanceSummary({
              advance_total: Number(raw.advance_total) || 0,
              already_reported: Number(raw.already_reported) || 0,
              remaining: Number(raw.remaining) || 0,
              reports_count: Number(raw.reports_count) || 0,
              draft_report_id: raw.draft_report_id || null,
            });
          } else setAdvanceSummary(null);
        } catch (_) {
          setAdvanceSummary(null);
        }
      } else setAdvanceSummary(null);
      setDialogOpen(true);
    } catch (e) {
      console.error("Error loading draft:", e);
      showError("Failed to load draft report");
    }
  };

  // Approval functions
  const handleApproveReport = async (report) => {
    setApproveDialogOpen(true);
    handleActionMenuClose();
  };

  const handleApproveConfirm = async () => {
    try {
      await travelExpenseReportsAPI.approveExpenseReport(selectedReport.id, {
        comments: approvalComments || undefined,
      });
      showSuccess("Expense report approved successfully");
      fetchExpenseReports();
      setApproveDialogOpen(false);
      setApprovalComments("");
    } catch (error) {
      console.error("Error approving expense report:", error);
      showError("Failed to approve expense report");
    }
  };

  const handleRejectReport = async (report) => {
    setRejectDialogOpen(true);
    handleActionMenuClose();
  };

  const handleRejectConfirm = async () => {
    try {
      if (!rejectionReason.trim()) {
        showError("Rejection reason is required");
        return;
      }

      await travelExpenseReportsAPI.rejectExpenseReport(selectedReport.id, {
        rejection_reason: rejectionReason,
      });
      showSuccess("Expense report rejected successfully");
      fetchExpenseReports();
      setRejectDialogOpen(false);
      setRejectionReason("");
    } catch (error) {
      console.error("Error rejecting expense report:", error);
      showError("Failed to reject expense report");
    }
  };

  const handleResubmitReport = async (report) => {
    try {
      await travelExpenseReportsAPI.resubmitExpenseReport(report.id);
      showSuccess("Expense report resubmitted successfully");
      fetchExpenseReports();
      handleActionMenuClose();
    } catch (error) {
      console.error("Error resubmitting expense report:", error);
      showError("Failed to resubmit expense report");
    }
  };

  const handleSubmitReport = async (report) => {
    try {
      await travelExpenseReportsAPI.submitExpenseReport(report.id);
      showSuccess("Expense report submitted successfully");
      setExpenseReports((prev) =>
        prev.map((r) => (String(r.id) === String(report.id) ? { ...r, status: "submitted" } : r))
      );
      fetchExpenseReports();
      handleActionMenuClose();
    } catch (error) {
      console.error("Error submitting expense report:", error);
      showError("Failed to submit expense report");
    }
  };

  // Summary cards data (two-level approval: submitted -> department_head_approved -> approved)
  const summaryCards = isApprovalView
    ? [
        {
          title: "Pending Department Head",
          value: expenseReports
            .filter((report) => report.status === "submitted")
            .length.toString(),
          icon: <PendingIcon />,
          color: "warning",
        },
        {
          title: "Pending Admin",
          value: expenseReports
            .filter((report) => report.status === "department_head_approved")
            .length.toString(),
          icon: <PendingIcon />,
          color: "primary",
        },
        {
          title: "Fully Approved",
          value: expenseReports
            .filter((report) => report.status === "approved")
            .length.toString(),
          icon: <ApprovedIcon />,
          color: "success",
        },
      ]
    : [
        {
          title: "Total Reports",
          value: expenseReports.length.toString(),
          icon: <ReportIcon />,
          color: "primary",
        },
        {
          title: "Pending Approval",
          value: expenseReports
            .filter((report) => report.status === "submitted" || report.status === "department_head_approved")
            .length.toString(),
          icon: <PendingIcon />,
          color: "warning",
        },
        {
          title: "Draft",
          value: expenseReports
            .filter((report) => report.status === "draft")
            .length.toString(),
          icon: <EditIcon />,
          color: "default",
        },
        {
          title: "Rejected",
          value: expenseReports
            .filter((report) => report.status === "rejected")
            .length.toString(),
          icon: <RejectedIcon />,
          color: "error",
        },
        {
          title: "Total Advance Amount",
          value: `TZS ${expenseReports
            .reduce(
              (sum, report) => sum + (report.total_advance_amount || 0),
              0
            )
            .toLocaleString()}`,
          icon: <MoneyIcon />,
          color: "success",
        },
      ];

  const totals = calculateTotals();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" component="h1" fontWeight="bold">
          {isApprovalView
            ? "Travel Expense Report Approvals"
            : "Travel Expense Reports"}
        </Typography>
        {!isApprovalView && !canCreateExpenseReport && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", maxWidth: 480 }}>
            <Typography variant="body2" color="text.secondary" component="span">
              As a supervisor you may only approve or reject reports submitted by employees.{" "}
            </Typography>
            <Button size="small" variant="outlined" onClick={() => navigate("/travel/expense-approvals")}>
              Open Travel Expense Report Approvals
            </Button>
          </Box>
        )}
        {!isApprovalView && canCreateExpenseReport && (
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Create expense report
          </Button>
        )}
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {summaryCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar sx={{ bgcolor: `${card.color}.main` }}>
                    {card.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {card.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {card.title}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Content: list of my expense reports */}
      <Card>
        <CardContent>
          {!isApprovalView && (
            <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 260 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All statuses</MenuItem>
                  {Object.entries(reportStatuses).map(([key, status]) => (
                    <MenuItem key={key} value={key}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <DataGrid
            rows={expenseReports}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            autoHeight
            getRowHeight={() => 100}
            sx={{ minHeight: 400 }}
          />
        </CardContent>
      </Card>

      {/* Create Expense Report Dialog: select travel first, then add expense lines */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { bgcolor: "background.paper" },
        }}
      >
        <DialogTitle>
          <Typography variant="h6">
            {editingDraftId ? "Edit draft expense report" : "New expense report"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {editingDraftId
              ? "You have a draft for this travel. Add or edit expense items, then save or submit."
              : "Select the travel, then add your expense items. Save as draft to continue later, or submit for approval when done."}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "background.paper" }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {/* Step 1: Select which travel this report is for */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1 }}>
                1. Select travel
              </Typography>
              <FormControl fullWidth size="small" sx={{ maxWidth: 480 }}>
                <InputLabel>Which travel is this report for?</InputLabel>
                <Select
                  value={formData.travel_request_id || ""}
                  onChange={(e) => {
                    const travelRequest = travelRequests.find(
                      (req) => req.id === e.target.value
                    );
                    if (travelRequest) {
                      handleTravelRequestSelect(travelRequest);
                    }
                  }}
                  label="Which travel is this report for?"
                >
                  <MenuItem value="">— Select travel —</MenuItem>
                  {(travelRequests || [])
                    .filter(
                      (req) =>
                        req.status === "admin_approved" ||
                        req.status === "approved" ||
                        req.approval_status === "approved"
                    )
                    .map((request) => (
                      <MenuItem key={request.id} value={request.id}>
                        {request.request_number || `#${request.id}`} – {request.destination || request.purpose || "Travel"}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              {!loading && (travelRequests || []).length === 0 && (
                <Alert severity="info" sx={{ mt: 1, maxWidth: 480 }}>
                  No approved travel requests. Submit a travel request and get it approved first, then you can create an expense report for it.
                </Alert>
              )}
              {formData.travel_request_id && formData.destination && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Destination: {formData.destination}
                    {formData.travel_purpose && ` · ${formData.travel_purpose}`}
                  </Typography>
                </Box>
              )}
            </Grid>
            {/* Step 2: Add expense items */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1 }}>
                2. Add expense items
              </Typography>
              <TextField
                fullWidth
                label="Description (optional)"
                value={formData.description}
                onChange={(e) =>
                  handleFormChange("description", e.target.value)
                }
                multiline
                rows={2}
                size="small"
                sx={{ mb: 2 }}
              />

              {/* Add Expense Line Form */}
              <Card sx={{ mb: 2, p: 2 }}>
                <input
                  type="file"
                  ref={receiptInputRef}
                  accept="image/*,.pdf"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleExpenseLineChange("receiptFile", file);
                      handleExpenseLineChange("receiptFileName", file.name);
                    }
                    e.target.value = "";
                  }}
                />
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="Expense Title"
                      value={newExpenseLine.title}
                      onChange={(e) =>
                        handleExpenseLineChange("title", e.target.value)
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={newExpenseLine.category}
                        onChange={(e) =>
                          handleExpenseLineChange("category", e.target.value)
                        }
                        label="Category"
                      >
                        {expenseCategories.map((category) => (
                          <MenuItem key={category.value} value={category.value}>
                            {category.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Amount"
                      type="number"
                      value={newExpenseLine.amount}
                      onChange={(e) =>
                        handleExpenseLineChange("amount", e.target.value)
                      }
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">TZS</InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      label="Location"
                      value={newExpenseLine.location}
                      onChange={(e) =>
                        handleExpenseLineChange("location", e.target.value)
                      }
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="contained"
                      onClick={addExpenseLine}
                      startIcon={<AddCircleIcon />}
                      size="small"
                    >
                      Add
                    </Button>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AttachFileIcon />}
                        onClick={() => receiptInputRef.current?.click()}
                      >
                        Attach receipt
                      </Button>
                      {newExpenseLine.receiptFileName && (
                        <>
                          <Typography variant="body2" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <InsertDriveFileIcon fontSize="small" />
                            {newExpenseLine.receiptFileName}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleExpenseLineChange("receiptFile", null);
                              handleExpenseLineChange("receiptFileName", "");
                            }}
                            color="error"
                            aria-label="Remove receipt"
                          >
                            <RemoveCircleIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Card>

              {/* Expense Lines Table */}
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Title</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Receipt</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(expenseLines || []).filter((line) => line != null).map((line, index) => (
                      <TableRow key={`expense-line-${index}-${line.id}`}>
                        <TableCell>{line.title ?? ""}</TableCell>
                        <TableCell>
                          {expenseCategories.find(
                            (cat) => cat.value === line.category
                          )?.label || (line.category ?? "")}
                        </TableCell>
                        <TableCell>
                          TZS {(line.amount != null ? Number(line.amount) : 0).toLocaleString()}
                        </TableCell>
                        <TableCell>{line.location ?? ""}</TableCell>
                        <TableCell>
                          {line.receiptFileName ? (
                            <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <InsertDriveFileIcon fontSize="small" color="action" />
                              {line.receiptFileName}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => removeExpenseLine(line.id)}
                            color="error"
                          >
                            <RemoveCircleIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary */}
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? theme.palette.grey[800]
                      : theme.palette.grey[50],
                  borderRadius: 1,
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      Advance (this travel): TZS{" "}
                      {totals.advanceAmount.toLocaleString()}
                    </Typography>
                    {totals.alreadyReported != null && totals.alreadyReported > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Already reported ({advanceSummary?.reports_count || 0} report(s)): TZS{" "}
                        {totals.alreadyReported.toLocaleString()}
                      </Typography>
                    )}
                    {totals.alreadyReported != null && totals.alreadyReported > 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Remaining before this report: TZS{" "}
                        {totals.remainingBeforeThis.toLocaleString()}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      This report total: TZS {totals.totalExpense.toLocaleString()}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ mt: 0.5 }}
                      color={
                        totals.remainingAfterThis >= 0 ? "success.main" : "error.main"
                      }
                    >
                      Remaining after this report: TZS{" "}
                      {totals.remainingAfterThis.toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, bgcolor: "background.paper" }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveDraft}
            variant="outlined"
            disabled={!formData.travel_request_id || expenseLines.length === 0}
          >
            Save as draft
          </Button>
          <Button
            onClick={handleSubmitForApproval}
            variant="contained"
            color="primary"
            disabled={!formData.travel_request_id || expenseLines.length === 0}
          >
            Submit for approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionMenuClose}
      >
        <MenuItem
          onClick={() => {
            setViewDialogOpen(true);
            handleActionMenuClose();
          }}
        >
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>

        {isApprovalView &&
          selectedReport &&
          ((user?.roles?.includes("Department Head") &&
            selectedReport.status === "submitted") ||
            (user?.roles?.includes("Admin") &&
              selectedReport.status === "department_head_approved")) && (
            <>
              <MenuItem
                onClick={() => handleApproveReport(selectedReport)}
                sx={{ color: "success.main" }}
              >
                <ListItemIcon>
                  <ApprovedIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText>Approve</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => handleRejectReport(selectedReport)}
                sx={{ color: "error.main" }}
              >
                <ListItemIcon>
                  <RejectedIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Reject</ListItemText>
              </MenuItem>
            </>
          )}

        {!isApprovalView &&
          selectedReport &&
          selectedReport.status === "rejected" &&
          isReportOwner(selectedReport) && (
            <MenuItem
              onClick={() => handleResubmitReport(selectedReport)}
              sx={{ color: "primary.main" }}
            >
              <ListItemIcon>
                <RefreshIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Resubmit</ListItemText>
            </MenuItem>
          )}

        {!isApprovalView &&
          selectedReport &&
          selectedReport.status === "draft" &&
          isReportOwner(selectedReport) && (
            <MenuItem
              onClick={() => handleEditDraft(selectedReport)}
              sx={{ color: "primary.main" }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Edit draft</ListItemText>
            </MenuItem>
          )}

        {!isApprovalView &&
          selectedReport &&
          selectedReport.status === "draft" &&
          isReportOwner(selectedReport) && (
            <MenuItem
              onClick={() => handleSubmitReport(selectedReport)}
              sx={{ color: "success.main" }}
            >
              <ListItemIcon>
                <ApprovedIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText>Submit for Approval</ListItemText>
            </MenuItem>
          )}
      </Menu>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          handleClosePreview();
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        <DialogTitle>
          <Typography variant="h6">Expense Report Details</Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "background.paper" }}>
          {viewDetailsLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <LoadingSpinner />
            </Box>
          )}
          {!viewDetailsLoading && (reportDetails || selectedReport) && (
            <>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Report Number
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {(reportDetails || selectedReport).report_number}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">
                    Title
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {(reportDetails || selectedReport).expense_title}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">
                    Destination
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {(reportDetails || selectedReport).destination || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={
                      reportStatuses[(reportDetails || selectedReport).status]?.label ||
                      (reportDetails || selectedReport).status
                    }
                    color={
                      reportStatuses[(reportDetails || selectedReport).status]?.color || "default"
                    }
                    sx={{ mb: 2 }}
                  />

                  <Typography variant="subtitle2" color="text.secondary">
                    Advance Amount
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    TZS {(reportDetails || selectedReport).total_advance_amount?.toLocaleString() ?? "0"}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">
                    Total Expenses
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    TZS {(reportDetails || selectedReport).total_expense_amount?.toLocaleString() ?? "0"}
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">
                    Remaining Amount
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    color={
                      (reportDetails || selectedReport).remaining_amount >= 0
                        ? "success.main"
                        : "error.main"
                    }
                  >
                    TZS {(reportDetails || selectedReport).remaining_amount?.toLocaleString() ?? "0"}
                  </Typography>
                </Grid>
              </Grid>

              {/* Expense lines (sub-items) */}
              {reportDetails?.expenseLines?.length > 0 && (
                <>
                  <Typography variant="subtitle1" fontWeight="600" sx={{ mt: 2, mb: 1 }}>
                    Expense Items
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Title</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell align="right">Amount (TZS)</TableCell>
                          <TableCell align="center">Receipt</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportDetails.expenseLines.map((line) => (
                          <TableRow key={line.id}>
                            <TableCell>{line.title}</TableCell>
                            <TableCell>
                              {expenseCategories.find((c) => c.value === line.category)?.label || line.category || "—"}
                            </TableCell>
                            <TableCell>{line.location || "—"}</TableCell>
                            <TableCell align="right">{Number(line.amount)?.toLocaleString() ?? "0"}</TableCell>
                            <TableCell align="center">
                              {line.document_id ? (
                                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", flexWrap: "wrap" }}>
                                  <Tooltip title="Preview">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        disabled={!!docLoading[`${line.document_id}-preview`]}
                                        onClick={() => handlePreviewReceipt(line.document_id, line.receipt_filename)}
                                      >
                                        {docLoading[`${line.document_id}-preview`]
                                          ? <CircularProgress size={16} color="primary" />
                                          : <ViewIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                  <Tooltip title="Download">
                                    <span>
                                      <IconButton
                                        size="small"
                                        disabled={!!docLoading[`${line.document_id}-download`]}
                                        onClick={() => handleDownloadReceipt(line.document_id, line.receipt_filename)}
                                      >
                                        {docLoading[`${line.document_id}-download`]
                                          ? <CircularProgress size={16} />
                                          : <AttachFileIcon fontSize="small" />}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              ) : line.has_receipt ? (
                                <Typography variant="caption" color="text.secondary">No file</Typography>
                              ) : (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: "background.paper" }}>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Document preview dialog */}
      <Dialog
        open={previewDoc.open}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        <DialogTitle>
          <Typography variant="subtitle1">{previewDoc.filename || "Document"}</Typography>
        </DialogTitle>
        <DialogContent>
          {previewDoc.blobUrl && (
            <>
              {previewDoc.isImage && (
                <Box component="img" src={previewDoc.blobUrl} alt="Receipt" sx={{ maxWidth: "100%", height: "auto" }} />
              )}
              {previewDoc.isPdf && !previewDoc.isImage && (
                <iframe
                  title="Document preview"
                  src={previewDoc.blobUrl}
                  width="100%"
                  height="600"
                  style={{ border: "none" }}
                />
              )}
              {!previewDoc.isImage && !previewDoc.isPdf && (
                <Typography color="text.secondary">
                  Preview not available. Use the download button in the report details to save the file.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
          {previewDoc.blobUrl && (
            <Button
              onClick={() => window.open(previewDoc.blobUrl, "_blank", "noopener,noreferrer")}
            >
              Open in new tab
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        <DialogTitle>
          <Typography variant="h6">Reject Expense Report</Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "background.paper" }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this expense report:{" "}
            <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter rejection reason (required)..."
            variant="outlined"
            required
            error={!rejectionReason.trim()}
            helperText={
              !rejectionReason.trim() ? "Rejection reason is required" : ""
            }
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: "background.paper" }}>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.paper" } }}
      >
        <DialogTitle>
          <Typography variant="h6">Approve Expense Report</Typography>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "background.paper" }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Add approval comments (optional):
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={approvalComments}
            onChange={(e) => setApprovalComments(e.target.value)}
            placeholder="Enter approval comments (optional)..."
            variant="outlined"
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: "background.paper" }}>
          <Button onClick={() => setApproveDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleApproveConfirm}
            color="success"
            variant="contained"
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TravelExpenseReportsPage;
